import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal-layout";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
});

function ContactPage() {
  return (
    <LegalLayout title="Contact Us" lastUpdated="December 24, 2025">
      <section className="space-y-6">
        <div>
          <p className="text-muted-foreground leading-relaxed mb-6">
            We're here to help! Whether you have questions, need support, or
            want to provide feedback, please don't hesitate to reach out.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-6">Contact Information</h2>

          <div className="space-y-6">
            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-3">
                📧 General Inquiries
              </h3>
              <p className="text-muted-foreground text-sm mb-3">
                For general questions about our service, features, or company:
              </p>
              <a
                href="mailto:support@images-ai.app"
                className="text-foreground hover:underline text-base font-medium"
              >
                support@images-ai.app
              </a>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-3">
                🛟 Customer Support
              </h3>
              <p className="text-muted-foreground text-sm mb-3">
                For technical issues, billing questions, or account help:
              </p>
              <a
                href="mailto:support@images-ai.app"
                className="text-foreground hover:underline text-base font-medium"
              >
                support@images-ai.app
              </a>
              <p className="text-sm text-muted-foreground mt-4">
                <strong>Response Time:</strong> We typically respond within
                24-48 hours on business days.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-3">
                🔒 Privacy & Data Protection
              </h3>
              <p className="text-muted-foreground text-sm mb-3">
                For privacy concerns, data protection requests, or GDPR-related
                inquiries:
              </p>
              <a
                href="mailto:support@images-ai.app"
                className="text-foreground hover:underline text-base font-medium"
              >
                support@images-ai.app
              </a>
              <p className="text-sm text-muted-foreground mt-4">
                Use this email to exercise your rights under GDPR (data access,
                deletion, portability, etc.). See our{" "}
                <Link to="/privacy" className="text-foreground hover:underline">
                  Privacy Policy
                </Link>{" "}
                for details.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-3">
                💼 Business Inquiries
              </h3>
              <p className="text-muted-foreground text-sm mb-3">
                For partnerships, enterprise solutions, or business
                opportunities:
              </p>
              <a
                href="mailto:support@images-ai.app"
                className="text-foreground hover:underline text-base font-medium"
              >
                support@images-ai.app
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-6">Postal Address</h2>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="font-medium mb-2">Harshil Agrawal</p>
            <p className="text-sm text-muted-foreground mb-3">Images AI</p>
            <p className="text-muted-foreground">Helene-Jacobs-Str.</p>
            <p className="text-muted-foreground">14199 Berlin</p>
            <p className="text-muted-foreground">Germany</p>
            <p className="text-sm text-muted-foreground mt-4">
              For complete legal information, please visit our{" "}
              <Link to="/impressum" className="text-foreground hover:underline">
                Impressum
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Before contacting us, you might find answers to common questions
            below:
          </p>

          <div className="space-y-4">
            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                How do I reset my password?
              </h3>
              <p className="text-muted-foreground text-sm">
                Visit the login page and click "Forgot Password." You'll receive
                an email with instructions to reset your password.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                How do I cancel my subscription?
              </h3>
              <p className="text-muted-foreground text-sm">
                You can cancel your subscription at any time through your
                account settings under Billing, or contact support at{" "}
                <a
                  href="mailto:support@images-ai.app"
                  className="text-foreground hover:underline"
                >
                  support@images-ai.app
                </a>
                . See our{" "}
                <Link to="/terms" className="text-foreground hover:underline">
                  Terms of Service
                </Link>{" "}
                for details.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                Do credits roll over to the next month?
              </h3>
              <p className="text-muted-foreground text-sm">
                No, credits reset at the beginning of each billing period and do
                not carry over. Unused credits expire at the end of your billing
                cycle.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                How do I delete my account and data?
              </h3>
              <p className="text-muted-foreground text-sm">
                To request account deletion and exercise your GDPR right to
                erasure, contact us at{" "}
                <a
                  href="mailto:support@images-ai.app"
                  className="text-foreground hover:underline"
                >
                  support@images-ai.app
                </a>
                . Your data will be deleted within 30 days, except where
                retention is required by law.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                Can I use generated images commercially?
              </h3>
              <p className="text-muted-foreground text-sm">
                Yes, you retain full ownership of all images you generate. You
                can use them for personal or commercial purposes. See our{" "}
                <Link to="/terms" className="text-foreground hover:underline">
                  Terms of Service
                </Link>{" "}
                for details.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                How do I report inappropriate content?
              </h3>
              <p className="text-muted-foreground text-sm">
                If you encounter content that violates our{" "}
                <Link
                  to="/acceptable-use"
                  className="text-foreground hover:underline"
                >
                  Acceptable Use Policy
                </Link>
                , please report it immediately to{" "}
                <a
                  href="mailto:support@images-ai.app"
                  className="text-foreground hover:underline"
                >
                  support@images-ai.app
                </a>{" "}
                with as much detail as possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-6">Report a Violation</h2>
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-2xl p-6">
            <h3 className="font-semibold mb-3">
              ⚠️ Report Illegal or Harmful Content
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed mb-4">
              If you've encountered illegal content, child exploitation
              material, or content that violates our{" "}
              <Link
                to="/acceptable-use"
                className="text-foreground hover:underline"
              >
                Acceptable Use Policy
              </Link>
              , please report it immediately:
            </p>
            <a
              href="mailto:support@images-ai.app?subject=Content%20Violation%20Report"
              className="text-foreground hover:underline text-base font-medium"
            >
              support@images-ai.app
            </a>
            <p className="text-sm text-muted-foreground mt-4">
              Include: Description of violation, link or identifier (if
              available), and any supporting evidence. We investigate all
              reports promptly and take appropriate action.
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-6">
            EU Consumer Dispute Resolution
          </h2>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="text-muted-foreground text-sm leading-relaxed mb-4">
              EU consumers may use the European Commission's Online Dispute
              Resolution platform for resolving disputes:
            </p>
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground hover:underline text-base font-medium"
            >
              https://ec.europa.eu/consumers/odr
            </a>
            <p className="text-sm text-muted-foreground mt-4">
              Our email address for the platform is listed above. We are not
              obligated to participate in dispute resolution proceedings before
              consumer arbitration boards but may do so voluntarily.
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-6">Response Times</h2>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>
                <strong>General Inquiries:</strong> 24-48 hours on business days
              </li>
              <li>
                <strong>Support Requests:</strong> 24-48 hours on business days
              </li>
              <li>
                <strong>Privacy/GDPR Requests:</strong> Within 30 days as
                required by GDPR
              </li>
              <li>
                <strong>Urgent Issues:</strong> We prioritize critical technical
                issues and security concerns
              </li>
            </ul>
            <p className="text-sm text-muted-foreground mt-6">
              <strong>Business Days:</strong> Monday - Friday, excluding German
              public holidays
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">We're Here to Help</h2>
          <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Your feedback and questions help us improve. We read every message
            and strive to provide helpful, timely responses. Thank you for
            choosing Images AI!
          </p>
        </div>
      </section>
    </LegalLayout>
  );
}
